import { useQueryClient } from '@tanstack/react-query';
import copyToClipboard from 'copy-to-clipboard';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { URL_PARAM_ASSETID } from '$const';
import { ROUTE_SEARCH } from '$routes';
import useFavouriteService from '~extension/favourite/hook/useFavouriteService';
import useAppDispatch from '~hooks/useAppDispatch';
import useAssets from '~hooks/useAssets';
import useLabels from '~hooks/useLabels';
import { useNavigateToPage } from '~hooks/useNavigateToPage';
import useUrlSearchParam from '~hooks/useUrlSearchParam';
import useMediaLibrarySearchQuery from '~modules/media-library/context/useMediaLibrarySearchQuery';
import useMediaLibrarySearchResult from '~modules/media-library/context/useMediaLibrarySearchResult';
import useMediaLibrarySettings from '~modules/media-library/context/useMediaLibrarySettings';
import useMediaLibraryUserPreferences from '~modules/media-library/context/useMediaLibraryUserPreferences';
import { postRestartContribution } from '~modules/media-library/controller/api/assets';
import { queries } from '~modules/media-library/controller/api/Queries';
import {
  onCloseContributionWizard,
  onOpenContributionWizard,
} from '~modules/media-library/controller/thunks/contribute';
import { AssetId } from '~modules/media-library/model/Asset';
import { AssetActions } from '~modules/media-library/model/AssetActions';
import { isAssetVideo, isCanvaTemplate } from '~modules/media-library/model/AssetFactory';
import { makeFilterOptionId } from '~modules/media-library/model/FilterHelper';
import { ViewContext } from '~modules/media-library/model/ViewContext';
import { objectSet } from '~services/immutable';
import { StatusActions } from '~store/actions';
import { makeProgramaticallyLinkClick } from '~utils/link';
import { hasOwnProperty, isObject } from '~utils/object';

type UseAssetActionsReturnValue = Resolve<
  {
    idDetails: AssetId | undefined;
    idDetailsNext: AssetId | undefined;
    idDetailsPrev: AssetId | undefined;
    idCollection: AssetId | undefined;
    idDownload: AssetId | undefined;
    idZoom: AssetId | undefined;
    idCrop: AssetId | undefined;
    idAddToMyfiles: AssetId | undefined;
    idWeb2Print: AssetId | undefined;
    idCanva: AssetId | undefined;
    idRaiseRequestForm: AssetId | undefined;
    idApprovalForm: AssetId | undefined;
    idDelete: AssetId | undefined;
    idJsmContactForm: AssetId | undefined;
  } & {
    closeDetails: () => void;
    closeCollection: () => void;
    closeDownload: () => void;
    closeZoom: () => void;
    closeCrop: () => void;
    closeAddToMyfiles: () => void;
    closeWeb2Print: () => void;
    closeCanva: () => void;
    closeRaiseRequest: () => void;
    closeContributionWizard: () => void;
    closeApprovalForm: () => void;
    closeConfirmDelete: () => void;
    closeJsmContactForm: () => void;
  } & AssetActions
>;

export default function useAssetActions(): UseAssetActionsReturnValue {
  const dispatch = useAppDispatch();
  // Read URL param and prepare collection state
  // derived values
  const { assetId, pageAlias, lang } = useParams();
  let paramAsset = useUrlSearchParam(URL_PARAM_ASSETID);
  if (assetId !== undefined) paramAsset = assetId;

  const queryClient = useQueryClient();

  const { getAssetById, setAsset } = useAssets();
  const { isFavouriteAddonEnabled, removeAssetFavourite, addAssetFavourite } = useFavouriteService();
  const { realm, idCollection, setCollectionId, unsetCollectionId, viewContext, setTopSearchTerm, searchTermTop } =
    useMediaLibrarySettings();
  const { searchQuery } = useMediaLibrarySearchQuery();
  const { showFavouritesOnly, onSetSelectedTabFilterId } = useMediaLibraryUserPreferences();
  const { resultAssetIds, onLoadMore, resetSearchResult } = useMediaLibrarySearchResult();
  const { term } = useParams();
  const searchTerm = term;

  const navigateToPage = useNavigateToPage();

  // Labels
  const { msgMediaLibraryCopyId, msgMediaLibraryCopyLink } = useLabels([
    'msgMediaLibraryCopyId',
    'msgMediaLibraryCopyLink',
  ]);

  // Prepare state
  const [idDetails, setIdDetails] = useState<AssetId>(paramAsset);
  const [idDetailsNext, setIdDetailsNext] = useState<AssetId>();
  const [idDetailsPrev, setIdDetailsPrev] = useState<AssetId>();
  const [idDownload, setIdDownload] = useState<AssetId>();
  const [idZoom, setIdZoom] = useState<AssetId>();
  const [idCrop, setIdCrop] = useState<AssetId>();
  const [idAddToMyfiles, setIdAddToMyfiles] = useState<AssetId>();
  const [idWeb2Print, setIdWeb2Print] = useState<AssetId>();
  const [idCanva, setIdCanva] = useState<AssetId>();
  const [idRaiseRequestForm, setIdRaiseRequestForm] = useState<AssetId>();
  const [idApprovalForm, setIdApprovalForm] = useState<AssetId>();
  const [idDelete, setIdDelete] = useState<AssetId>();
  const [idJsmContactForm, setIdJsmContactForm] = useState<AssetId>();

  // Handler: copy asset id to clipboard
  const onCopyId = useCallback(
    (id: AssetId) => {
      const asset = getAssetById(id);
      if (!asset) return;
      const idToCopy = isAssetVideo(asset) ? asset.attributes.kalturaId || asset.id : asset.id;
      const isCopied = copyToClipboard(idToCopy);
      if (isCopied) {
        dispatch(StatusActions.setSuccessNotification(msgMediaLibraryCopyId));
      }
    },
    [dispatch, msgMediaLibraryCopyId, getAssetById],
  );

  // Handler: copy asset link to clipboard
  const onCopyLink = useCallback(
    (id: AssetId) => {
      // default, for search page
      const currentHref = window.location.href.split(/[?#]/)[0];
      let urlToAsset = `${currentHref}?${URL_PARAM_ASSETID}=${id}`;
      // for content pages
      if (pageAlias) {
        urlToAsset = `${window.location.origin}/${lang ? lang + '/' : ''}brandguide/${pageAlias}/asset/${id}`;
      }
      const isCopied = copyToClipboard(urlToAsset);
      if (isCopied) {
        dispatch(StatusActions.setSuccessNotification(msgMediaLibraryCopyLink));
      }
    },
    [dispatch, lang, msgMediaLibraryCopyLink, pageAlias],
  );
  // Handler: onDetails opens details modal
  const onDetails = useCallback(
    (id: AssetId) => {
      // invalidate query cache to force reload
      queryClient.invalidateQueries({ ...queries.mediaLibrary.details(id) });

      const indexAssetId = resultAssetIds.indexOf(id);
      if (indexAssetId < 0) return;
      setIdDetails(id);

      const prevIndexAssetId = indexAssetId - 1;
      const prevAssetId = resultAssetIds[prevIndexAssetId];
      setIdDetailsPrev(prevAssetId);

      const nextIndexAssetId = indexAssetId + 1;
      const nextAssetId = resultAssetIds[nextIndexAssetId];
      setIdDetailsNext(nextAssetId);

      // make sure to always load more results so that there is we get all ids
      if (resultAssetIds.length - indexAssetId < 10) {
        onLoadMore?.();
      }
    },
    [queryClient, resultAssetIds, onLoadMore],
  );

  const stableActions = useMemo(
    () => ({
      closeDetails: () => {
        setIdDetails(undefined);
        // Remove ASSET_PARAM from url
        window.history.replaceState({}, '', `${window.location.pathname}`);
      },
      // Handler: onOpenCollection
      onCollection: (id: AssetId) => {
        // make sure every modal is closed
        setIdDetails(undefined);
        setIdDownload(undefined);
        setIdZoom(undefined);
        setIdCrop(undefined);
        setIdAddToMyfiles(undefined);
        setIdWeb2Print(undefined);
        setIdCanva(undefined);
        setIdRaiseRequestForm(undefined);
        setIdApprovalForm(undefined);
        setIdJsmContactForm(undefined);
        // then enter the collection
        setCollectionId(id);

        setTopSearchTerm(searchTerm);
        // clear search string on open collection on search page
        if (viewContext === ViewContext.SEARCH) {
          navigateToPage(ROUTE_SEARCH);
        }
      },
      closeCollection: () => {
        if (viewContext === ViewContext.SEARCH) {
          const searchTerm = searchTermTop !== '' ? searchTermTop : null;
          navigateToPage(ROUTE_SEARCH, { searchTerm });
        }
        unsetCollectionId();
      },
      // Handler: onDownload opens share dialog with action download
      onDownload: (id: AssetId) => setIdDownload(id),
      closeDownload: () => setIdDownload(undefined),
      // Handler: onZoom opens zoom dialog
      onZoom: (id: AssetId) => setIdZoom(id),
      closeZoom: () => setIdZoom(undefined),
      // Handler: onCrop opens crop dialog
      onCrop: (id: AssetId) => setIdCrop(id),
      closeCrop: () => setIdCrop(undefined),
      // Handler: onAddToMyfiles opens share dialog with action addToMyfiles
      onAddToMyfiles: (id: AssetId) => setIdAddToMyfiles(id),
      closeAddToMyfiles: () => setIdAddToMyfiles(undefined),
      onUseWeb2Print: (id: AssetId) => setIdWeb2Print(id),
      onUseRaiseRequestAssets: (id: AssetId) => setIdRaiseRequestForm(id),
      onJsmContactForm: (id: AssetId) => setIdJsmContactForm(id),
      closeWeb2Print: () => setIdWeb2Print(undefined),
      closeCanva: () => setIdCanva(undefined),
      closeRaiseRequest: () => setIdRaiseRequestForm(undefined),

      closeContributionWizard: () => {
        dispatch(onCloseContributionWizard());
        queryClient.invalidateQueries();
      },
      openContributionWizard: (id: AssetId, contributionMode: string) => {
        dispatch(onOpenContributionWizard({ id, contributionMode }));
      },
      onApprovalForm: (id: AssetId) => setIdApprovalForm(id),
      // Handler: onConfirmDelete opens share dialog with action delete
      onConfirmDelete: (id: AssetId) => setIdDelete(id),
      closeConfirmDelete: () => setIdDelete(undefined),
      closeJsmContactForm: () => setIdJsmContactForm(undefined),
    }),
    [
      setCollectionId,
      setTopSearchTerm,
      searchTerm,
      viewContext,
      navigateToPage,
      unsetCollectionId,
      searchTermTop,
      dispatch,
      queryClient,
    ],
  );

  const onUseCanva = useCallback(
    async (id: AssetId) => {
      const asset = getAssetById(id);
      if (!asset || !isCanvaTemplate(asset)) return;

      if (asset.infos.skipCanvaIntro) {
        const href =
          'https://www.canva.com/login?redirect=%2Fdesign%3Fcreate%3Dtrue%26template%3D' + asset.attributes.sourceId;
        makeProgramaticallyLinkClick({ href });
        setIdCanva(undefined);
      } else {
        setIdCanva(id);
      }
    },
    [getAssetById],
  );

  // Handler: onToggleFavourite add/remove asset from favourites list
  const onToggleFavourite = useCallback(
    async (id: AssetId) => {
      const asset = getAssetById(id);
      if (!asset) return;

      try {
        const response = asset.infos.isFavourite ? await removeAssetFavourite(id) : await addAssetFavourite(id);
        setAsset(objectSet(asset, 'infos.isFavourite', !asset.infos.isFavourite));

        // reload result list if favourite filter is set
        if ((viewContext === ViewContext.FAVOURITE || showFavouritesOnly) && asset.infos.isFavourite) {
          queryClient.invalidateQueries({ ...queries.mediaLibrary.search(realm, searchQuery, idCollection) });
        }

        if (response.message) {
          dispatch(StatusActions.setSuccessNotification(response.message));
        }
      } catch (err) {
        if (isObject(err) && hasOwnProperty(err, 'message')) {
          dispatch(StatusActions.setErrorNotification(err.message));
          return;
        }
        if (isObject(err) && hasOwnProperty(err, 'error') && typeof err.error === 'string') {
          dispatch(StatusActions.setErrorNotification(err.error));
          return;
        }
      }
    },
    [
      getAssetById,
      removeAssetFavourite,
      addAssetFavourite,
      setAsset,
      viewContext,
      showFavouritesOnly,
      queryClient,
      realm,
      searchQuery,
      idCollection,
      dispatch,
    ],
  );

  // Handler: onPlay open video in new tab
  const onPlay = useCallback(
    async (id: AssetId) => {
      const asset = getAssetById(id);
      if (!asset) return;
      if (!isAssetVideo(asset)) return;
      const href = asset.attributes.videoPreview;
      makeProgramaticallyLinkClick({ href });
    },
    [getAssetById],
  );

  const closeApprovalForm = useCallback(async () => {
    setIdApprovalForm(undefined);
    // also close detail view
    setIdDetails(undefined);
    // invalidate query cache to force reload
    queryClient.invalidateQueries();
  }, [queryClient]);

  const onDeleteSuccess = useCallback(async () => {
    setIdDelete(undefined);
    setIdDetails(undefined);
    // invalidate query cache to force reload
    queryClient.invalidateQueries();
  }, [queryClient]);

  const onRestartContribution = useCallback(
    async (assetId: AssetId) => {
      const res: any = await postRestartContribution(assetId);
      if (res.status && res.status === true) {
        setIdDetails(undefined);
        // invalidate query cache to force reload
        queryClient.invalidateQueries();
        onSetSelectedTabFilterId(makeFilterOptionId('WorkflowState', 'draft'));
      }
    },
    [onSetSelectedTabFilterId, queryClient],
  );

  const onUploadSuccess = useCallback(async () => {
    resetSearchResult?.();
  }, [resetSearchResult]);

  return useMemo<UseAssetActionsReturnValue>(
    () => ({
      // IDs
      idDetails,
      idDetailsNext,
      idDetailsPrev,
      idCollection,
      idDownload,
      idZoom,
      idCrop,
      idAddToMyfiles,
      idWeb2Print,
      idCanva,
      idRaiseRequestForm,
      idApprovalForm,
      idDelete,
      idJsmContactForm,
      // Actions
      onDetails,
      onCopyId,
      onCopyLink,
      onToggleFavourite: isFavouriteAddonEnabled() ? onToggleFavourite : undefined,
      onPlay,
      onUseCanva,
      closeApprovalForm,
      onDeleteSuccess,
      onRestartContribution,
      onUploadSuccess,
      ...stableActions,
    }),
    [
      idDetails,
      idDetailsNext,
      idDetailsPrev,
      idCollection,
      idDownload,
      idZoom,
      idCrop,
      idAddToMyfiles,
      idWeb2Print,
      idCanva,
      idRaiseRequestForm,
      idApprovalForm,
      idDelete,
      idJsmContactForm,
      onDetails,
      onCopyId,
      onCopyLink,
      isFavouriteAddonEnabled,
      onToggleFavourite,
      onPlay,
      onUseCanva,
      closeApprovalForm,
      onDeleteSuccess,
      onRestartContribution,
      onUploadSuccess,
      stableActions,
    ],
  );
}
